import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MobileShell } from '../components/MobileShell';
import { ChevronLeft, Languages } from 'lucide-react';
import TranslatorPanel from '../components/TranslatorPanel';

/**
 * Full-screen dedicated translator page.
 * Replaces the previous inline panel inside Chuy chat (which was fighting for
 * vertical space with the chat, input dock, quick prompts and bottom nav).
 */
export default function TranslatorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();

  const close = () => {
    // If /translator was opened as a fresh session (deep link, share, home-screen
    // shortcut), history has no previous page — go to Chuy chat instead of a blank.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/chuy-chat');
    }
  };

  return (
    <MobileShell withBottomNav={false} className="!pb-0">
      <div
        className="flex flex-col"
        style={{
          minHeight: '100dvh',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        data-testid="translator-page"
      >
        {/* Header */}
        <header className="px-4 py-3 flex items-center gap-3 border-b border-border bg-background/95 backdrop-blur-md shrink-0">
          <button
            type="button"
            onClick={close}
            data-testid="translator-page-back"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
            aria-label="Back"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="w-10 h-10 rounded-full bg-caribbean/15 text-caribbean flex items-center justify-center shrink-0">
            <Languages size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-base leading-tight">
              {language === 'en' ? 'Translator' : 'Traductor'}
            </h1>
            <p className="text-[11px] text-muted-foreground truncate">
              {language === 'en'
                ? 'Break the language barrier with Chuy AI'
                : 'Rompe la barrera del idioma con Chuy AI'}
            </p>
          </div>
        </header>

        {/* Not-signed-in gate */}
        {!user && (
          <div className="flex-1 flex items-center justify-center px-6 text-center">
            <div>
              <div className="mx-auto w-16 h-16 rounded-full bg-caribbean/10 text-caribbean flex items-center justify-center mb-4">
                <Languages size={28} />
              </div>
              <h2 className="font-heading font-bold text-lg mb-2">
                {language === 'en' ? 'Sign in to translate' : 'Inicia sesión para traducir'}
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                {language === 'en'
                  ? 'Free translator powered by Chuy AI. 15+ languages including Spanish, English, Chinese, Russian, Arabic, Japanese, and more.'
                  : 'Traductor gratis impulsado por Chuy AI. Más de 15 idiomas: español, inglés, chino, ruso, árabe, japonés y más.'}
              </p>
              <button
                type="button"
                onClick={() => navigate('/auth?next=/translator')}
                data-testid="translator-page-signin"
                className="h-11 px-6 rounded-full bg-caribbean hover:bg-caribbean/90 text-white text-sm font-semibold"
              >
                {language === 'en' ? 'Sign in' : 'Iniciar sesión'}
              </button>
            </div>
          </div>
        )}

        {/* Translator body — reuses the shared panel component in full-page mode */}
        {user && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <TranslatorPanel fullPage />
          </div>
        )}
      </div>
    </MobileShell>
  );
}
